import type { getInvoiceInformation } from '@/actions/invoice-information';
import { BillingDetails } from '@/lib/stripe/StripeProvider';
import { ActionFnData } from '@/types/response-types';

/**
 * Type derived from the return type of getInvoiceInformation
 */
export type InvoiceInformationType = ActionFnData<typeof getInvoiceInformation>;

/**
 * Options for constructing billing details.
 */
export interface BuildBillingDetailsOptions {
    invoiceInformation?: InvoiceInformationType | null;
    sessionUser?: {
        email?: string | null;
        name?: string | null;
    } | null;
    fallbackEmail?: string;
}

/**
 * Constructs a BillingDetails object from invoice information, session user data,
 * and optional fallback email. Used to pre-fill Stripe payment forms.
 *
 * Priority:
 * - Email: invoiceInformation.invoiceEmail > sessionUser.email > fallbackEmail
 * - Name: sessionUser.name
 * - Company Name: invoiceInformation.name
 * - VAT Number: invoiceInformation.vatNumber
 * - Address: invoiceInformation.address
 */
export function buildBillingDetails({
    invoiceInformation,
    sessionUser,
    fallbackEmail,
}: BuildBillingDetailsOptions): BillingDetails {
    return {
        email: invoiceInformation?.invoiceEmail || sessionUser?.email || fallbackEmail || undefined,
        name: sessionUser?.name || undefined,
        companyName: invoiceInformation?.name || undefined,
        vatNumber: invoiceInformation?.vatNumber || undefined,
        address: invoiceInformation?.address
            ? {
                  line1: invoiceInformation.address.street || undefined,
                  city: invoiceInformation.address.city || undefined,
                  postal_code: invoiceInformation.address.postalCode || undefined,
                  country: invoiceInformation.address.country?.alpha2Code || undefined,
              }
            : undefined,
    };
}

export const buildInvoiceInformationFormData = (invoiceInformation: NonNullable<InvoiceInformationType>) => {
    return {
        type: invoiceInformation.invoicingType,
        personalName: invoiceInformation.invoicingType === 'PERSONAL' ? invoiceInformation.name || '' : '',
        companyName: invoiceInformation.invoicingType === 'COMPANY' ? invoiceInformation.name || '' : '',
        vatNumber: invoiceInformation.vatNumber || '',
        street: invoiceInformation.address?.street || '',
        city: invoiceInformation.address?.city || '',
        postalCode: invoiceInformation.address?.postalCode || '',
        countryCode: invoiceInformation.address?.country?.alpha2Code || '',
    };
};
